// ** React Imports
import { useMemo, useState } from "react";

// ** Redux
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import queryString from "query-string";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import ShowMoreButton from "components/layout/ShowMoreButton";
import LocationLink from "../LocationChips/components/components/LocationLink";

// ** Custom Hooks
import useQuery from "hooks/useQuery";
import {
	compute as filterPreferencesCompute,
	rawItemsNew as rawPreferencesItems,
} from "hooks/filters/useFilterPreferences";

// ** Utils & Helpers
import generatePath from "utils/generatePath";

// ** Initial Query
import { initialQuery } from "containers/Users/initialQuery";

// ** Styled Components
import { StyledLink, StyledText, StyledArrow } from "../styles";

// #####################################################

const MAX_INITIAL_PREFERENCES = 15;

// #####################################################

const PreferencesChips = ({
	preferenceUrl,
	locationPath,
	locationChipsVisible,
}) => {
	const { t } = useTranslation(["pages", "filtersAndSelectors"]);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	// ** Odczytanie parametrów z query
	const query = useQuery();
	const { preferences: preferenceQueryUrl } = query || {};

	// ** Zapisanie obiektu query jako string
	const queryToString = isAuth
		? Object.keys(query).length
			? queryString.stringify({ ...query, page: 1 })
			: queryString.stringify({ ...initialQuery })
		: "";

	const [expanded, setExpanded] = useState(false);

	// ** ID bieżącej preferencji
	const preferenceId = useMemo(() => {
		return preferenceUrl
			? filterPreferencesCompute.urlToApiParam(preferenceUrl)
			: preferenceQueryUrl;
	}, [preferenceUrl, preferenceQueryUrl]);

	// **
	const linkTo = useMemo(() => {
		return `/search/preferences/${
			preferenceUrl ||
			filterPreferencesCompute.valueToUrl(Number(preferenceId))
		}`;
	}, [preferenceId]);

	// Filtrowane i sortowane preferencje
	const filteredAndSortedPreferences = useMemo(() => {
		if (!rawPreferencesItems?.length) return [];
		return [...rawPreferencesItems]
			.filter((item) => item?.key !== preferenceId)
			.sort((a, b) => {
				const nameA = t(`filtersAndSelectors:preferences.${a.key}`);
				const nameB = t(`filtersAndSelectors:preferences.${b.key}`);
				return new Intl.Collator("pl", { sensitivity: "base" }).compare(
					nameA,
					nameB
				);
			});
	}, [rawPreferencesItems, preferenceId, t]);

	// Określamy, które preferencje wyświetlić
	const preferencesToDisplay = useMemo(() => {
		if (expanded) {
			return filteredAndSortedPreferences;
		}
		return filteredAndSortedPreferences.slice(0, MAX_INITIAL_PREFERENCES);
	}, [expanded, filteredAndSortedPreferences]);

	const preferencesHidden = useMemo(() => {
		return filteredAndSortedPreferences.slice(MAX_INITIAL_PREFERENCES);
	}, [filteredAndSortedPreferences]);

	// Czy przycisk "Pokaż więcej" powinien być widoczny
	const showMoreButtonVisible =
		filteredAndSortedPreferences.length > MAX_INITIAL_PREFERENCES;

	// #####################################################

	return (
		<Block flex column withRowGap>
			<Block flex flexWrap mt={locationChipsVisible ? 2 : 0}>
				<StyledText>
					<StyledLink to="/search/preferences">
						{t("filtersAndSelectors:preferencesLabel")}
					</StyledLink>
				</StyledText>
				<StyledArrow />
				<StyledText>
					<StyledText bold>
						{preferenceId && (
							<StyledLink
								to={{
									pathname: linkTo,
									state: {
										closeFiltersAfterLinkClick: true,
									},
									search: isAuth ? queryToString : undefined,
								}}
							>
								{t(
									`filtersAndSelectors:preferences.${preferenceId}`
								)}
							</StyledLink>
						)}
					</StyledText>
				</StyledText>
			</Block>
			{rawPreferencesItems.length > 0 && preferenceUrl && !isAuth && (
				<Block flex column withRowGap={"var(--dz-spacing-xs)"}>
					<Block>
						<Text small bold>
							{t("common:breadcrumbs.otherPreferences")}
						</Text>
					</Block>
					<Block
						flex
						flexWrap
						withColumnGap={"var(--dz-spacing-xxs)"}
						withRowGap={"var(--dz-spacing-xxs)"}
					>
						{preferencesToDisplay.map((preferenceItem, index) => {
							const generatedPath = generatePath(locationPath, {
								preference: preferenceItem?.code,
							});
							return (
								<LocationLink
									key={`preference-${preferenceItem.code}`}
									name={t(
										`filtersAndSelectors:preferences.${preferenceItem.key}`
									)}
									to={generatedPath}
									onClick={() => setExpanded(false)}
									isLast={
										index ===
										preferencesToDisplay.length - 1
									}
								/>
							);
						})}
						{!expanded && showMoreButtonVisible && (
							<Text small>...</Text>
						)}
						{!expanded && (
							<Block hide>
								{preferencesToDisplay.map((preferenceItem) => {
									const generatedPath = generatePath(
										locationPath,
										{
											preference: preferenceItem?.code,
										}
									);
									return (
										<LocationLink
											key={`preference-${preferenceItem.code}`}
											name={t(
												`filtersAndSelectors:preferences.${preferenceItem.key}`
											)}
											to={generatedPath}
											onClick={() =>
												setExpanded(!expanded)
											}
										/>
									);
								})}
							</Block>
						)}
					</Block>
					{showMoreButtonVisible && (
						<Block flex justifyEnd>
							<ShowMoreButton
								onClick={() => setExpanded(!expanded)}
							>
								{expanded
									? t("common:showLess")
									: t("common:showMore")}
							</ShowMoreButton>
						</Block>
					)}
				</Block>
			)}
		</Block>
	);
};

// #####################################################

export default PreferencesChips;

// #####################################################
